
import * as _ from 'lodash';
import { cloneElement, Component } from 'react';
import { Alert, Modal } from 'react-bootstrap';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { createStructuredSelector } from 'reselect';

import { PolicyType } from '../../../typings/enums';
import { Action } from '../../../typings/permissions';
import { WithDialogContext } from '../../contexts/DialogContext';
import { window } from '../../globals';
import { userHasPermissionForPolicyType } from '../../helpers/permissions';
import PolicyModule from '../../redux/policies';
import utils from '../../utils';
import Button, { ButtonGroup } from '../generic/Button';
import { FormGroup, LabeledTextArea, LabeledTextInput } from '../generic/Forms';
import Icon, { icons } from '../generic/Icon';
import type { DialogState } from '../../contexts/DialogContext';
import type { AsyncStatus, Policy, RouterProps } from '../../types';

import ListPoliciesCallToAction from './ListPoliciesCallToAction';

const policyTypeMap = {
  QUALITY: 'quality',
  LICENSING: 'licensing',
  SECURITY: 'security',
  SBOM: 'sbom',
};

type DispatchProps = {
  deletePolicy: (policyId: number) => Promise<void>;
  updatePolicy: (policy: Policy) => Promise<void>;
  fetchPolicies: () => Promise<void>;
};

type Props = RouterProps & DispatchProps & {
  policies: Record<number, Policy>;
  dialogContext: DialogState;
  status: AsyncStatus
};

type ListPoliciesPageState = {
  editModalState?: Policy | null;
  error?: string | null;
};

class ListPoliciesPage extends Component<Props, ListPoliciesPageState> {
  constructor(props: Props) {
    super(props);
    this.state = {
      editModalState: null,
      error: null,
    };
  }

  componentDidMount() {
    this.props.fetchPolicies();
  }

  onRemovePolicy = (policyId: number) => {
    if (!utils.verify_subscription('premium')) {
      window.showPlanSelectorModal();
      return;
    }
    this.props.dialogContext.onConfirm({
      title: 'Confirm Delete Policy',
      body: (
        <div>
          Are you sure you want to delete this policy? This will remove the policy from any projects it is assigned to.
          <b>This cannot be undone.</b>
        </div>
      ),
      onConfirm: () => this.props.deletePolicy(policyId),
    });
  };

  onExportPolicy = (policy: Policy) => {
    if (policy.type !== PolicyType.LICENSING) {
      return;
    }

    window.open(`/api/policies/${policy.id}/export`, '_blank', 'noopener=true');
  };

  onEditPolicy = (policy: Policy) => {
    if (!utils.verify_subscription('premium')) {
      window.showPlanSelectorModal();
      return;
    }

    this.setState({ editModalState: policy });
  };

  onSubmitPolicyEdit = () => {
    if (!this.state.editModalState) {
      return;
    }
    this.props.updatePolicy(this.state.editModalState).then(() => this.setState({ editModalState: null }));
  };

  onCloseModal = () => {
    this.setState({ editModalState: null });
  };

  onPolicyTitleChange = (newTitle: string) =>
    this.setState((previousState) => (
      { editModalState: { ...previousState.editModalState, title: newTitle } }
    ));

  onPolicyDescriptionChange = (newDescription: string) =>
    this.setState((previousState) => (
      { editModalState: { ...previousState.editModalState, description: newDescription } }
    ));

  renderPolicyList = (type: PolicyType) => {
    //  Note: we are using _.filter here because props.policies is of type `Record<number, Policy>`
    const policyList = _.filter(this.props.policies, (policy: Policy) => policy && policy.type === type)
      .map((policy: Policy) => {
        if (!policy.id) {
          return (
            <li className="text-center clickable animated fadeInUp" key={policy.id}>
              <a>
                <h4 className="policy-list-item-header">
                  {policy.title}
                  <p>Pending...</p>
                </h4>
              </a>
            </li>
          );
        }

        const hasDeletePermission = userHasPermissionForPolicyType(Action.Delete, type);
        const hasEditPermission = userHasPermissionForPolicyType(Action.Edit, type);

        return (
          <li className="text-center clickable animated fadeInDown" key={`$policy-${policy.id}`}>
            <Link to={`/policies/${policyTypeMap[type]}/${policy.id}`}>
              <div style={{ backgroundColor: utils.hashColor(policy.id, 0.7) }} />
              <h4 className="policy-list-item-header">
                {policy.title || <i>Untitled</i>}
                <p>{(policy.rules || []).length} Rules</p>
                {(hasDeletePermission || hasEditPermission || policy.type === PolicyType.LICENSING) && (
                  <span onClick={utils.stopEventPropagation} className="hover-nav animated fadeInUp quick">

                    {/* Delete Button */}
                    {hasDeletePermission &&
                      <button
                        type="button"
                        onClick={() => this.onRemovePolicy(policy.id)}
                        className="btn-circle btn btn-danger">
                        <Icon icon={icons.times} />
                      </button>}

                    {/* Edit Button */}
                    {hasEditPermission &&
                      <button
                        type="button"
                        onClick={() => this.onEditPolicy(policy)}
                        className="btn-circle btn">
                        <Icon icon={icons.pencil} />
                      </button>}

                    {/* Export Button  */}
                    {policy.type === PolicyType.LICENSING &&
                      <button
                        type="button"
                        onClick={() => this.onExportPolicy(policy)}
                        className="btn-circle btn">
                        <Icon icon={icons.fileExport} />
                      </button>}
                  </span>
                )}
              </h4>
            </Link>
          </li>
        );
      });
    return (
      <div>
        <ul className="list-clean list-horiz policy-list">
          {policyList.length > 0 ? policyList : <div className="subtitle">No {policyTypeMap[type]} policies yet.</div>}
        </ul>
      </div>
    );
  };

  render() {
    const {
      children,
      params,
      status,
      location,
    } = this.props;
    return (
      <>
        <div>
          <Helmet title="Policies" />
          {!params.policyId && (
            <ListPoliciesCallToAction location={location} />
          )}
          {this.props.children &&
            cloneElement(children, {
              params,
              status,
              onRemovePolicy: this.onRemovePolicy,
              policies: this.props.policies,
              renderPolicyList: this.renderPolicyList,
            })}
        </div>
        <Modal show={!!this.state.editModalState} onHide={this.onCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>Edit {`${_.capitalize(this.state.editModalState?.type)}`} Policy</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {this.state.error && <Alert bsStyle="error">{this.state.error}</Alert>}
            <form className="form-horizontal">
              <FormGroup>
                <LabeledTextInput
                  label="Title"
                  placeholder="Mobile App Policy"
                  value={this.state.editModalState?.title}
                  onChange={this.onPolicyTitleChange}
                  testId="policyTitleForm"
                />
                <LabeledTextArea
                  label="Description"
                  placeholder="Standard policy for all mobile apps distributed by Company X."
                  value={this.state.editModalState?.description}
                  onChange={this.onPolicyDescriptionChange}
                  testId="policyDescriptionForm"
                />
              </FormGroup>
            </form>
          </Modal.Body>
          <Modal.Footer>
            <ButtonGroup>
              <Button
                type="secondary"
                size="medium"
                onClick={this.onCloseModal}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                size="medium"
                onClick={this.onSubmitPolicyEdit}
              >
                Submit
              </Button>
            </ButtonGroup>
          </Modal.Footer>
        </Modal>
      </>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  policies: PolicyModule.selectors.getPolicies,
  status: PolicyModule.selectors.getStatus,
});

const mapDispatchToProps = {
  fetchPolicies: PolicyModule.actions.fetchAll,
  deletePolicy: PolicyModule.actions.delete,
  updatePolicy: PolicyModule.actions.update,
};

const ListPoliciesPageWithDialog = WithDialogContext(ListPoliciesPage);

export default connect(mapStateToProps, mapDispatchToProps)(ListPoliciesPageWithDialog);
